import { Signal, computed, signal } from '@angular/core';
import { CategoryPageConfiguration } from 'src/app/category/interfaces/category-page.interface';
import { ObservableState } from 'src/app/core/constants/observable-state.interface';
import { SEO } from 'src/app/core/services/seo/seo.service';
import { HomeContentMappedProductList } from 'src/app/home/models/home-content.model';

// Interfaces for data models
export interface IProduct {
    id: string;
    name: string;
    display_name: string;
    sku: string;
    brand: Brand;
    mrp: number;
    tags: any[];
    one_line_description: string;
    description: string;
    short_description: string;
    highlights: string[];
    featured_images: featured_image[];
    configurations: any[];
    categories: Category[];
    offers: any[];
    price_list: PriceList;
    tax: string;
    is_active: boolean;
    product_type: 'goods' | 'services';
    variant_group: VariantGroup;
    variant_values: VariantValue[];
    buyer_groups: any[];
    sales_channel: string[];
    selling_price: number;
    is_archived: boolean;
    is_disabled: boolean;
    is_exposed: boolean;
    is_visible: boolean;
    can_order: boolean;
    is_allow_back_orders: boolean;
    max_quantity_per_transaction: number;
    attributes: any[];
    downloads: string | null;
    global_message: string;
    assets: string | null;
    features: string | null;
    products_in_box: string;
    questions_and_answers: string;
    seo_detail: SEO;
    shipping_info: string;
    specifications: any[];
    reviews: string;
    product_id: string;
    images: string[];
    url_path: string;
    in_stock: boolean;
    variants: Variant[];
    // Add missing properties that are used by components
    frequently_bought_together?: any[];
    sponsored_products?: any[];
    similar_products?: any[];
    accessories?: any[];
    stock?: Stock;
    bidding?: Bidding;
    product_in_box?: string | null;
    deal?: ProductDealCountdown;
    weight?: number;
    dimensions?: string;
    unit?: Unit;
    manufacturer?: string;
    country_of_origin?: string;
}

export interface IVariantGroup {
    id: string;
    name: string;
    description: string;
    product_type: string;
    is_active: boolean;
    primary_attribute: string;
}

export interface IBidding {
    min_price: string;
    max_price: string;
    is_bidding_active: boolean;
    is_low_bid: boolean;
}

export interface IDownloads {
    user_manuals?: Downloadable[];
    catalog?: Downloadable[];
    // Add other properties as needed
}

export interface IDownloadable {
    id: string;
    name: string;
    url: string;
    file_size: number;
    file_type: string;
    created_at: string;
    updated_at: string;
}

export interface IFeature {
    id: string;
    name: string;
    description: string;
    icon?: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface IPriceList {
    id: string;
    name: string;
    price: number;
    currency: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface IRecommendedOffer {
    id: string;
    name: string;
    description: string;
    discount_type: string;
    discount_value: number;
    minimum_order_value: number;
    maximum_discount: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface IImage {
    id: string;
    url: string;
    alt_text: string;
    caption: string;
    is_primary: boolean;
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

export interface ProductList {
    count: number;
    url: string;
}

export interface Product {
    id: string;
    name: string;
    display_name: string;
    sku: string;
    brand: Brand;
    mrp: number;
    tags: any[];
    one_line_description: string;
    description: string;
    short_description: string;
    highlights: string[];
    featured_images: featured_image[];
    configurations: any[];
    categories: Category[];
    offers: any[];
    price_list: PriceList;
    tax: string;
    is_active: boolean;
    product_type: 'goods' | 'services';
    variant_group: VariantGroup;
    variant_values: VariantValue[];
    buyer_groups: any[];
    sales_channel: string[];
    selling_price: number;
    is_archived: boolean;
    is_disabled: boolean;
    is_exposed: boolean;
    is_visible: boolean;
    can_order: boolean;
    is_allow_back_orders: boolean;
    max_quantity_per_transaction: number;
    attributes: any[];
    downloads: string | null;
    global_message: string;
    assets: string | null;
    features: string | null;
    products_in_box: string;
    questions_and_answers: string;
    seo_detail: SEO;
    shipping_info: string;
    specifications: any[];
    reviews: string;
    product_id: string;
    images: string[];
    url_path: string;
    in_stock: boolean;
    variants: Variant[];
    frequently_bought_together_products: ProductList;
    stock?: Stock;
    bidding?: Bidding;
    product_in_box?: string | null;
    deal?: ProductDealCountdown;
    weight?: number;
    dimensions?: string;
    unit?: Unit;
    manufacturer?: string;
    country_of_origin?: string;
    // Add missing properties that are used by components
    frequently_bought_together?: any[];
    sponsored_products?: any[];
    similar_products?: any[];
    accessories?: any[];
}

export class Product implements Product {
    constructor(
        public id: string,
        public name: string,
        public display_name: string,
        public sku: string,
        public brand: Brand,
        public mrp: number,
        public tags: any[],
        public one_line_description: string,
        public description: string,
        public short_description: string,
        public highlights: string[],
        public featured_images: featured_image[],
        public configurations: any[],
        public categories: Category[],
        public offers: any[],
        public price_list: PriceList,
        public tax: string,
        public is_active: boolean,
        public product_type: 'goods' | 'services',
        public variant_group: VariantGroup,
        public variant_values: VariantValue[],
        public buyer_groups: any[],
        public sales_channel: string[],
        public selling_price: number,
        public is_archived: boolean,
        public is_disabled: boolean,
        public is_exposed: boolean,
        public is_visible: boolean,
        public can_order: boolean,
        public is_allow_back_orders: boolean,
        public max_quantity_per_transaction: number,
        public attributes: any[],
        public downloads: string | null,
        public global_message: string,
        public assets: string | null,
        public features: string | null,
        public products_in_box: string,
        public questions_and_answers: string,
        public seo_detail: SEO,
        public shipping_info: string,
        public specifications: any[],
        public reviews: string,
        public product_id: string,
        public images: string[],
        public url_path: string,
        public in_stock: boolean,
        public variants: Variant[],
        public frequently_bought_together?: any[],
        public similar_products?: any[],
        public sponsored_products?: any[],
        public accessories?: any[],
        public stock?: Stock,
        public bidding?: Bidding,
        public product_in_box?: string | null,
        public deal?: ProductDealCountdown,
        public weight?: number,
        public dimensions?: string,
        public unit?: Unit,
        public manufacturer?: string,
        public country_of_origin?: string
    ) {

    }
}

export class VariantGroup {
    id: string = '';
    name: string = '';
    description: string = '';
    product_type: string = '';
    is_active: boolean = false;
    primary_attribute: string = '';
}

export class Bidding {
    min_price: string = '';
    max_price: string = '';
    is_bidding_active: boolean = false;
    is_low_bid: boolean = false;
}

export class Downloads {
    constructor(
        public user_manuals?: Downloadable[],
        public catalog?: Downloadable[],
        public price_list?: Downloadable[],
        public howToGuide?: Downloadable[]
    ) { }
}
export class Downloadable {
    constructor(
        public name?: string,
        public link?: string
    ) { }
}

export class Feature {
    constructor(
        public title?: string,
        public description?: string,
        public images?: Image[],
    ) { }
}

export interface PriceList {
    other_offers: OtherOffers[];
    recommended_offers: RecommendedOffer;
}

export class PriceList implements PriceList {
    constructor(
        public other_offers: OtherOffers[],
        public recommended_offers: RecommendedOffer
    ) { }
}
export class RecommendedOffer {
    constructor(
        public applicable_amount: number,
        public buyer_groups: any[],
        public sales_channel: any[],
        public applied_offer_type: any,
        public discount_rate: number,
        public discount_type: string,
        public max_allowed_quantity: number,
        public max_allowed_inventory: number,
        public maximum_offer_amount_per_transaction: number,
        public minimum_quantity: number,
        public offer_id: string,
        public offer_name: string,
        public offer_type: any,
        public percent_savings: number,
        public savings: number,
        public description: string,
        public terms: string,
        public start_date: string,
        public end_date: string,
        public bundle_item_quantity?: number,
        public bundle_item?: Product,
        public cash_points?: number
    ) { }
}

export class Image {
    constructor(
        public url?: string,
        public tags?: string[],
        public position?: number
    ) { }
}
export class Category {
    constructor(
        public id?: string,
        public name?: string,
        public sub_categories?: Category[],
        public category_images?: any
    ) { }
}
export class Variants {
    constructor(
        public url?: string,
        public count?: number,
        public variants?: Variant[]
    ) { }
}

export class Variant {
    constructor(
        public is_active: boolean,
        public is_visible: boolean,
        public is_archived: boolean,
        public featured_images: featured_image[],
        public product_id: string,
        public is_disabled: boolean,
        public sales_channel: string[],
        public is_allow_back_orders: boolean,
        public can_order: boolean,
        public variant_values: VariantValue[],
        public is_exposed: boolean,
        public url_path: string,
        public in_stock: boolean,
        public display_name: string,
        public id?: string,
        public price_list?: {
            recommended_offers: {
                applicable_amount: number
            }
        },
        public variant_product_available?: boolean
        /* ,
        public variants?: Record<string, string> */
    ) { }
}

export class Stock {
    constructor(
        public in_stock?: boolean,
        public total_available_stock?: string,
        public stock_in_nearest_branch?: string,
        public stock_by_branches?: string,
        public committed_stock?: number,
        public opening_stock?: number,
        public physical_committed_stock?: number,
        public quantity_to_be_invoiced?: number,
        public quantity_to_be_shipped?: number,
        public total_physical_available_stock?: number
    ) { }
}

export interface Assets {
    images?: AssetImages;
    videos?: AssetVideos;

}
export interface AssetImages {
    featuredImages?: Image[];
    feature?: Image[];
    others?: Image[];
}
export class AssetVideos {
    featured?: Video[];
    others?: Video[];
}

export interface featured_image {
    url: string,
    position: number,
    tags: []
}
export class Video extends Image {
}

export class VariantValue {
    value: string = '';
    key: string = '';
}

interface Banner {
    type: string;
    position: string;
    banner_image_url: string;
}

export interface CategoryBanner {
    banners: Banner[];
    sales_channel: {
        name: string;
        id: string;
    };
}

export interface CategoryDetail {
    id: string;
    name: string;
    display_name?: any;
    parent_category?: any;
    category_images: string[];
    url_path: string;
    position: number;
    category_banners: CategorySectionConfiguration[];
    description: string;
    meta_title?: string;
    meta_description?: string;
    search_keywords?: string[];
    category_page_configurations?: {
        saved_category_page_configurations?: CategoryPageConfiguration[];
        published_category_page_configurations?: CategoryPageConfiguration[];
    }
}

export interface CategorySectionConfiguration {
    configurations: {
        show_product_list: boolean;
        show_filters: boolean;
    },
    sales_channel: CategoryBannerSalesChannel;
    sections: CategorySection[]
}

export interface CategorySection extends HomeContentMappedProductList {
    banners: BannerDetails[];
}

export interface BannerDetails {
    position: string;
    banner_image_url: string;
    type: string;
}

export interface CategoryBannerSalesChannel {
    id: string;
    name: string;
}

export interface Brand {
    id?: string;
    name?: string;
    display_name?: string;
    logo?: any;
    description?: string;
    is_active?: boolean;
    created_at?: Date;
    updated_at?: Date;
    created_by?: string;
    last_modified_by?: string;
    owner?: string;
}

export class Brand implements Brand {
    constructor(
        public id?: string,
        public name?: string,
        public display_name?: string,
        public logo?: any,
        public description?: string,
        public is_active?: boolean,
        public created_at?: Date,
        public updated_at?: Date,
        public created_by?: string,
        public last_modified_by?: string,
        public owner?: string
    ) {

    }
}

export interface ProductDealCountdown {
    is_active: boolean;
    start_time: string;
    end_time: string;
    description?: string;
    text_color?: string;
    badge_message: string;
    background_color?: string;
}

export interface AiChatReply {
    statusCode: number;
    headers: any;
    body: string;
}


export interface OtherOffers {
    end_date: string
    sales_channel: string[]
    description: any
    applicable_amount: number
    discount_type: string
    uses_per_buyer: number
    discount_rate: number
    offer_id: string
    offer_name: string
    maximum_offer_amount_per_transaction: number
    max_allowed_inventory: number
    buyer_groups: string[]
    bundle_item: BundleItem
    bundle_item_quantity: number
    terms: any
    percent_savings: number
    savings: number
    minimum_quantity: number
    offer_type: string
    start_date: string
    cash_points: any
}

export interface BundleItem {
    selling_price: number
    name: string
    id: string
    sku: string
}

export interface Unit {
    id: string;
    display_name: string;
    name: string;
}

export interface Collection {
    id: string;
    name: string;
    description: string;
    company: string;
    image: string;
    url_path: string;
    is_active: boolean;
    is_archived: boolean;
    position: number;
    seo_details: any;
}

// Signal-based Product State Management
export interface ProductState {
    product: Product | null;
    features: Signal<ObservableState<Feature[]>>;
    downloads: Signal<ObservableState<Downloads>>;
    assets: Signal<ObservableState<Assets>>;
    productsInABox: Signal<ObservableState<string[]>>;
    frequentlyBoughtTogether: Signal<ObservableState<Product[]>>;
    sponsoredProducts: Signal<ObservableState<Product[]>>;
    similarProducts: Signal<ObservableState<Product[]>>;
    accessories: Signal<ObservableState<Product[]>>;
    loading: boolean;
    error: string | null;
}

// Combined state interface for better signal management
interface DataState<T> {
    data: T;
    loading: boolean;
    error: string | null;
}

export class ProductStateManager {
    private _product = signal<Product | null>(null);

    // Combined state signals - much cleaner approach
    private _featuresState = signal<DataState<Feature[]>>({
        data: [],
        loading: false,
        error: null
    });

    private _downloadsState = signal<DataState<Downloads | null>>({
        data: null,
        loading: false,
        error: null
    });

    private _assetsState = signal<DataState<Assets | null>>({
        data: null,
        loading: false,
        error: null
    });

    private _productsInABoxState = signal<DataState<string[]>>({
        data: [],
        loading: false,
        error: null
    });

    private _frequentlyBoughtTogetherState = signal<DataState<Product[]>>({
        data: [],
        loading: false,
        error: null
    });

    private _sponsoredProductsState = signal<DataState<Product[]>>({
        data: [],
        loading: false,
        error: null
    });

    private _similarProductsState = signal<DataState<Product[]>>({
        data: [],
        loading: false,
        error: null
    });

    private _accessoriesState = signal<DataState<Product[]>>({
        data: [],
        loading: false,
        error: null
    });

    private _loading = signal<boolean>(false);
    private _error = signal<string | null>(null);

    // Public readonly signals - full state objects
    readonly product = this._product.asReadonly();
    readonly featuresState = this._featuresState.asReadonly();
    readonly downloadsState = this._downloadsState.asReadonly();
    readonly assetsState = this._assetsState.asReadonly();
    readonly productsInABoxState = this._productsInABoxState.asReadonly();
    readonly frequentlyBoughtTogetherState = this._frequentlyBoughtTogetherState.asReadonly();
    readonly sponsoredProductsState = this._sponsoredProductsState.asReadonly();
    readonly similarProductsState = this._similarProductsState.asReadonly();
    readonly accessoriesState = this._accessoriesState.asReadonly();

    // Computed signals for easy data access
    readonly features = computed(() => this._featuresState().data);
    readonly downloads = computed(() => this._downloadsState().data);
    readonly assets = computed(() => this._assetsState().data);
    readonly productsInABox = computed(() => this._productsInABoxState().data);
    readonly frequentlyBoughtTogether = computed(() => this._frequentlyBoughtTogetherState().data);
    readonly sponsoredProducts = computed(() => this._sponsoredProductsState().data);
    readonly similarProducts = computed(() => this._similarProductsState().data);
    readonly accessories = computed(() => this._accessoriesState().data);

    // Computed signals for loading states
    readonly featuresLoading = computed(() => this._featuresState().loading);
    readonly downloadsLoading = computed(() => this._downloadsState().loading);
    readonly assetsLoading = computed(() => this._assetsState().loading);
    readonly productsInABoxLoading = computed(() => this._productsInABoxState().loading);
    readonly frequentlyBoughtTogetherLoading = computed(() => this._frequentlyBoughtTogetherState().loading);
    readonly sponsoredProductsLoading = computed(() => this._sponsoredProductsState().loading);
    readonly similarProductsLoading = computed(() => this._similarProductsState().loading);
    readonly accessoriesLoading = computed(() => this._accessoriesState().loading);

    // Computed signals for error states
    readonly featuresError = computed(() => this._featuresState().error);
    readonly downloadsError = computed(() => this._downloadsState().error);
    readonly assetsError = computed(() => this._assetsState().error);
    readonly productsInABoxError = computed(() => this._productsInABoxState().error);
    readonly frequentlyBoughtTogetherError = computed(() => this._frequentlyBoughtTogetherState().error);
    readonly sponsoredProductsError = computed(() => this._sponsoredProductsState().error);
    readonly similarProductsError = computed(() => this._similarProductsState().error);
    readonly accessoriesError = computed(() => this._accessoriesState().error);

    // Computed signal for overall loading state
    readonly anyLoading = computed(() =>
        this._featuresState().loading ||
        this._downloadsState().loading ||
        this._assetsState().loading ||
        this._productsInABoxState().loading ||
        this._frequentlyBoughtTogetherState().loading ||
        this._sponsoredProductsState().loading ||
        this._similarProductsState().loading ||
        this._accessoriesState().loading
    );

    readonly loading = this._loading.asReadonly();
    readonly error = this._error.asReadonly();

    // Computed signals for derived state
    readonly isProductLoaded = computed(() => !!this._product());
    readonly hasFeatures = computed(() => !this._featuresState().loading && !this._featuresState().error && !!this._featuresState().data?.length);
    readonly hasDownloads = computed(() => !this._downloadsState().loading && !this._downloadsState().error && !!this._downloadsState().data);
    readonly hasAssets = computed(() => !this._assetsState().loading && !this._assetsState().error && !!this._assetsState().data);
    readonly hasProductsInABox = computed(() => !this._productsInABoxState().loading && !this._productsInABoxState().error && !!this._productsInABoxState().data?.length);
    readonly hasRelatedProducts = computed(() => {
        const similar = this._similarProductsState();
        const frequently = this._frequentlyBoughtTogetherState();
        const sponsored = this._sponsoredProductsState();
        const accessories = this._accessoriesState();
        return (!similar.loading && !similar.error && !!similar.data?.length) ||
            (!frequently.loading && !frequently.error && !!frequently.data?.length) ||
            (!sponsored.loading && !sponsored.error && !!sponsored.data?.length) ||
            (!accessories.loading && !accessories.error && !!accessories.data?.length);
    });

    // Update methods
    setProduct(product: Product) {
        this._product.set(product);
    }

    // Pure signal setters with loading state management
    setFeaturesLoading(loading: boolean) {
        this._featuresState.update(state => ({ ...state, loading, error: loading ? null : state.error }));
    }

    setFeatures(features: Feature[]) {
        this._featuresState.set({ data: features, loading: false, error: null });
    }

    setFeaturesError(error: string) {
        this._featuresState.update(state => ({ ...state, loading: false, error }));
    }

    setDownloadsLoading(loading: boolean) {
        this._downloadsState.update(state => ({ ...state, loading, error: loading ? null : state.error }));
    }

    setDownloads(downloads: Downloads) {
        this._downloadsState.set({ data: downloads, loading: false, error: null });
    }

    setDownloadsError(error: string) {
        this._downloadsState.update(state => ({ ...state, loading: false, error }));
    }

    setAssetsLoading(loading: boolean) {
        this._assetsState.update(state => ({ ...state, loading, error: loading ? null : state.error }));
    }

    setAssets(assets: Assets) {
        this._assetsState.set({ data: assets, loading: false, error: null });
    }

    setAssetsError(error: string) {
        this._assetsState.update(state => ({ ...state, loading: false, error }));
    }

    setProductsInABoxLoading(loading: boolean) {
        this._productsInABoxState.update(state => ({ ...state, loading, error: loading ? null : state.error }));
    }

    setProductsInABox(productsInABox: string[]) {
        this._productsInABoxState.set({ data: productsInABox, loading: false, error: null });
    }

    setProductsInABoxError(error: string) {
        this._productsInABoxState.update(state => ({ ...state, loading: false, error }));
    }

    // Setter methods for related products sections
    setFrequentlyBoughtTogetherLoading(loading: boolean) {
        this._frequentlyBoughtTogetherState.update(state => ({ ...state, loading, error: loading ? null : state.error }));
    }

    setFrequentlyBoughtTogether(products: Product[]) {
        this._frequentlyBoughtTogetherState.set({ data: products, loading: false, error: null });
    }

    setFrequentlyBoughtTogetherError(error: string) {
        this._frequentlyBoughtTogetherState.update(state => ({ ...state, loading: false, error }));
    }

    setSponsoredProductsLoading(loading: boolean) {
        this._sponsoredProductsState.update(state => ({ ...state, loading, error: loading ? null : state.error }));
    }

    setSponsoredProducts(products: Product[]) {
        this._sponsoredProductsState.set({ data: products, loading: false, error: null });
    }

    setSponsoredProductsError(error: string) {
        this._sponsoredProductsState.update(state => ({ ...state, loading: false, error }));
    }

    setSimilarProductsLoading(loading: boolean) {
        this._similarProductsState.update(state => ({ ...state, loading, error: loading ? null : state.error }));
    }

    setSimilarProducts(products: Product[]) {
        this._similarProductsState.set({ data: products, loading: false, error: null });
    }

    setSimilarProductsError(error: string) {
        this._similarProductsState.update(state => ({ ...state, loading: false, error }));
    }

    setAccessoriesLoading(loading: boolean) {
        this._accessoriesState.update(state => ({ ...state, loading, error: loading ? null : state.error }));
    }

    setAccessories(products: Product[]) {
        this._accessoriesState.set({ data: products, loading: false, error: null });
    }

    setAccessoriesError(error: string) {
        this._accessoriesState.update(state => ({ ...state, loading: false, error }));
    }

    setLoading(loading: boolean) {
        this._loading.set(loading);
    }

    setError(error: string | null) {
        this._error.set(error);
    }

    // Reset all state
    reset() {
        this._product.set(null);
        this._featuresState.set({ data: [], loading: false, error: null });
        this._downloadsState.set({ data: null, loading: false, error: null });
        this._assetsState.set({ data: null, loading: false, error: null });
        this._productsInABoxState.set({ data: [], loading: false, error: null });
        this._frequentlyBoughtTogetherState.set({ data: [], loading: false, error: null });
        this._sponsoredProductsState.set({ data: [], loading: false, error: null });
        this._similarProductsState.set({ data: [], loading: false, error: null });
        this._accessoriesState.set({ data: [], loading: false, error: null });
        this._loading.set(false);
        this._error.set(null);
    }
}

// Utility function to create a default Product object
export function createDefaultProduct(): Product {
    return {
        id: '',
        name: '',
        display_name: '',
        sku: '',
        brand: { id: '', name: '', is_active: false, created_at: new Date(), updated_at: new Date(), created_by: '', last_modified_by: '', owner: '' },
        mrp: 0,
        tags: [],
        one_line_description: '',
        description: '',
        short_description: '',
        highlights: [],
        featured_images: [],
        configurations: [],
        categories: [],
        offers: [],
        price_list: { other_offers: [], recommended_offers: { applicable_amount: 0, buyer_groups: [], sales_channel: [], applied_offer_type: null, discount_rate: 0, discount_type: '', max_allowed_quantity: 0, max_allowed_inventory: 0, maximum_offer_amount_per_transaction: 0, minimum_quantity: 0, offer_id: '', offer_name: '', offer_type: null, percent_savings: 0, savings: 0, description: '', terms: '', start_date: '', end_date: '' } },
        tax: '',
        is_active: false,
        product_type: 'goods',
        variant_group: { id: '', name: '', description: '', product_type: '', is_active: false, primary_attribute: '' },
        variant_values: [],
        buyer_groups: [],
        sales_channel: [],
        selling_price: 0,
        is_archived: false,
        is_disabled: false,
        is_exposed: false,
        is_visible: false,
        can_order: false,
        is_allow_back_orders: false,
        max_quantity_per_transaction: 0,
        attributes: [],
        downloads: null,
        global_message: '',
        assets: null,
        features: null,
        products_in_box: '',
        questions_and_answers: '',
        seo_detail: { meta_title: '', meta_description: '', keywords: [], also_known_as_names: [] },
        shipping_info: '',
        specifications: [],
        reviews: '',
        product_id: '',
        images: [],
        url_path: '',
        in_stock: false,
        variants: [],
        similar_products: [],
        frequently_bought_together_products: { count: 0, url: '' },
        sponsored_products: [],
        accessories: []
    };
}